import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toPriceStr} from '@/utils';
import {useAgentDataStore} from './agent-data.store';
import AgentDateRangeFilter, {useAgentDateRange} from './AgentDateRangeFilter';
import AgentTabLoading from './agent-tab-loading';
import stickySurfaceStyles from './refer-earn-sticky-surface.module.scss';
import {useStickyActive} from './use-sticky-active';

const columns = [
  'userId',
  'depositAmount',
  'depositType',
  'bonusRate',
  'bonus',
  'depositTime',
] as const;
const scrollColumns = columns.slice(1);
const fixedColumnWidthClassName = 'w-[6rem]';
const scrollOffsetClassName = 'ml-[6rem]';
const scrollGridClassName = 'grid-cols-[7.25rem_7rem_6rem_5rem_9rem]';
const scrollMinWidthClassName = 'min-w-[34.25rem]';
const headerGradientClassName = 'bg-[#FFEDB5]';
const stickyFirstColumnClassName =
  'flex h-10 items-center truncate pl-3 pr-3 text-left';
const scrollCellClassName = 'min-w-0 truncate px-3 text-left';

const formatAmount = (value?: string | number) =>
  toPriceStr(Number(value || 0) || 0, {thousands: true, fixed: 2});

const DepositBonusTableSkeleton = () => (
  <div aria-hidden="true" className="relative z-[1] animate-pulse">
    {Array.from({length: 4}).map((_, index) => (
      <div
        key={index}
        className={[
          'grid h-10 items-center text-[0.75rem]',
          scrollGridClassName,
          scrollMinWidthClassName,
          index % 2 === 1 ? 'bg-[rgba(140,140,140,0.06)]' : '',
        ].join(' ')}>
        {Array.from({length: scrollColumns.length}).map((__, cellIndex) => (
          <div key={cellIndex} className={scrollCellClassName}>
            <div className="h-3 w-3/5 rounded-full bg-[#FFE8A1]/70" />
          </div>
        ))}
      </div>
    ))}
  </div>
);

const DepositBonusContent = () => {
  const {t} = useTranslation();
  const [keyword, setKeyword] = useState('');
  const {range, dateRangeValue, setDateRangeValue} = useAgentDateRange();
  const depositBonusList = useAgentDataStore(state => state.depositBonusList);
  const depositBonusLoading = useAgentDataStore(
    state => state.depositBonusLoading,
  );
  const loadDepositBonusData = useAgentDataStore(
    state => state.loadDepositBonusData,
  );
  const {active: stickyActive, sentinelRef} = useStickyActive();
  const rows = depositBonusList?.content || [];
  const showSkeleton = depositBonusLoading && !rows.length;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadDepositBonusData({
        ...range,
        userId: keyword ? Number(keyword) || undefined : undefined,
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyword, loadDepositBonusData, range]);

  return (
    <section className="px-0 pb-4">
      <span ref={sentinelRef} aria-hidden="true" className="block h-px" />
      <div
        className={[
          stickyActive ? stickySurfaceStyles.stickySurface : '',
          stickyActive ? stickySurfaceStyles.dataControlsSurface : '',
          'sticky top-[3.0625rem] z-20 -mx-2 grid grid-cols-[minmax(0,180fr)_minmax(0,160fr)] gap-[0.625rem] bg-transparent px-2 py-[0.875rem]',
        ].join(' ')}>
        <AgentDateRangeFilter
          className="w-full"
          range={range}
          value={dateRangeValue}
          onChange={setDateRangeValue}
        />
        <label className="relative flex h-12 min-w-0 items-center gap-[0.1875rem] rounded-lg bg-[rgba(255,255,255,0.72)] px-2 shadow-[inset_0_0_0_0.0625rem_rgba(255,255,255,0.45)] backdrop-blur-[0.125rem]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1.25rem"
            height="1.25rem"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
            className="shrink-0">
            <path
              d="M12.7338 13.5204L11.9389 12.7246C10.9954 13.4977 9.78999 13.962 8.4762 13.9635C5.45197 13.9635 3 11.5091 3 8.4818C3 5.45446 5.45197 3 8.4762 3C11.5004 3 13.9524 5.45446 13.9524 8.4818C13.9508 9.78738 13.4933 10.986 12.7317 11.9274L13.5282 12.7246L13.5102 12.7421L13.5171 12.7363C13.689 12.5997 13.9397 12.6114 14.0989 12.7702L16.8719 15.546C17.0427 15.7171 17.0427 15.995 16.8719 16.1665L16.1674 16.8718C15.9965 17.0427 15.7189 17.0427 15.5476 16.8718L12.7745 14.0959C12.6158 13.9371 12.6042 13.6861 12.7407 13.5135L12.7338 13.5204ZM8.4762 4.31247C6.17602 4.31247 4.31113 6.17926 4.31113 8.4818C4.31113 10.7843 6.17602 12.6511 8.4762 12.6511C10.7764 12.6511 12.6412 10.7843 12.6412 8.4818C12.6391 6.17979 10.7758 4.31459 8.4762 4.31247Z"
              fill="black"
            />
          </svg>
          <input
            type="search"
            aria-label={t('invitation.depositBonus.searchPlaceholder')}
            placeholder={t('invitation.depositBonus.searchPlaceholder')}
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[0.75rem] font-normal leading-none text-black outline-none placeholder:text-black"
          />
        </label>
      </div>

      <div className="relative mt-0 min-h-[6.875rem] overflow-hidden rounded-[0.625rem] border-[0.5px] border-white/25 bg-white">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 top-[1.875rem] z-[2] rounded-b-[0.625rem] border-x-[0.5px] border-b-[0.5px] border-white/25"
        />
        <span
          aria-hidden="true"
          className={[
            'pointer-events-none absolute inset-x-0 top-0 z-[1] h-[1.875rem] rounded-t-[0.5rem] shadow-[inset_0_0.0625rem_0_rgba(121,251,255,0.07)]',
            headerGradientClassName,
          ].join(' ')}
        />
        <AgentTabLoading active={depositBonusLoading && !showSkeleton} />
        <div
          className={[
            'absolute left-0 top-0 z-[3]',
            fixedColumnWidthClassName,
          ].join(' ')}>
          <div className="flex h-[1.875rem] items-center truncate pl-3 pr-3 text-left text-[0.75rem] font-semibold leading-[0.9375rem] tracking-[-0.01875rem] text-[#564715]">
            {t('invitation.depositBonus.columns.userId')}
          </div>
          {rows.map((row, index) => (
            <div
              key={`${row.userId}-${row.depositTime}-${index}`}
              className={[
                stickyFirstColumnClassName,
                'text-[0.75rem] font-normal leading-[1.144rem] text-black',
                index % 2 === 1 ? 'bg-[rgba(140,140,140,0.06)]' : 'bg-white',
              ].join(' ')}>
              {row.userId}
            </div>
          ))}
          {showSkeleton &&
            Array.from({length: 4}).map((_, index) => (
              <div
                key={index}
                className={[
                  stickyFirstColumnClassName,
                  index % 2 === 1 ? 'bg-[rgba(140,140,140,0.06)]' : 'bg-white',
                ].join(' ')}>
                <span className="h-3 w-3/5 rounded-full bg-[#FFE8A1]/70" />
              </div>
            ))}
        </div>
        <div className="relative z-[1]">
          <div
            className={[
              'overflow-x-auto [overscroll-behavior-x:contain] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              scrollOffsetClassName,
            ].join(' ')}>
            <div
              className={[
                'grid h-[1.875rem] items-center',
                scrollGridClassName,
                scrollMinWidthClassName,
              ].join(' ')}>
              {scrollColumns.map(column => (
                <div
                  key={column}
                  className="min-w-0 whitespace-nowrap px-3 text-left text-[0.75rem] font-semibold leading-[0.9375rem] tracking-[-0.01875rem] text-[#564715]">
                  {t(`invitation.depositBonus.columns.${column}`)}
                </div>
              ))}
            </div>
            {showSkeleton ? (
              <DepositBonusTableSkeleton />
            ) : (
              rows.map((row, index) => (
                <div
                  key={`${row.userId}-${row.depositTime}-${index}`}
                  className={[
                    'grid h-10 items-center text-[0.75rem] font-normal leading-[1.144rem] text-black',
                    scrollGridClassName,
                    scrollMinWidthClassName,
                    index % 2 === 1 ? 'bg-[rgba(140,140,140,0.06)]' : '',
                  ].join(' ')}>
                  <div className={scrollCellClassName}>
                    {formatAmount(row.depositAmount)}
                  </div>
                  <div className={scrollCellClassName}>
                    {t(`invitation.depositBonus.types.${row.depositType}`, {
                      defaultValue: row.depositType,
                    })}
                  </div>
                  <div className={scrollCellClassName}>{row.bonusRate}</div>
                  <div className={scrollCellClassName}>
                    {formatAmount(row.bonus)}
                  </div>
                  <div className={scrollCellClassName}>{row.depositTime}</div>
                </div>
              ))
            )}
          </div>
        </div>
        {!rows.length && !depositBonusLoading && (
          <div className="relative z-[1] flex h-20 items-center justify-center text-[0.75rem] text-[#857866]">
            {t('invitation.agentData.noData')}
          </div>
        )}
      </div>
    </section>
  );
};

export default DepositBonusContent;
